import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ConsequenceRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetConsequencesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC consequence data to match the GraphQL query structure
 */
export function mapTrpcConsequencesToGraphQL(
  trpcData: ConsequenceRegisterResponse | undefined
): GetConsequencesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    consequence: trpcData.consequence,
  };
}

export const useGetConsequenceRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.consequence.register.queryOptions(),

    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcConsequencesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
  };
};
